import { type AuthUser, type IntranetQuickAccessItem } from '@gb-ticket/shared-types';
import type { LucideIcon } from 'lucide-react';

import { platformAdminLinkGroups } from '@/platform/lib/admin';
import { masterDataStartGroups } from '@/platform/lib/master-data-paths';
import {
  activeProductCode,
  productMenuPaths,
  productMenuTiles,
  productNavRegistration,
  type ProductMenuTile,
} from '@/platform/lib/product-nav';
import { productIcon } from '@/platform/lib/product-icon';
import { platformProductTiles, START_PATH } from '@/platform/lib/products';

export type OverviewTile = {
  id: string;
  path: string;
  label: string;
  group?: string;
  icon?: LucideIcon;
};

export type MenuModuleId = string;

export type MenuModule = {
  id: MenuModuleId;
  label: string;
  soon?: boolean;
};

export function defaultOverviewPaths(user: AuthUser): string[] {
  return platformProductTiles(user)
    .filter((tile) => tile.status === 'live' && tile.href)
    .map((tile) => tile.href!);
}

export function stammdatenMenuTiles(user: AuthUser): ProductMenuTile[] {
  return masterDataStartGroups(user).flatMap((group) =>
    group.links.map((link) => ({
      id: `stammdaten-${link.id}`,
      label: link.label,
      to: link.to,
    })),
  );
}

export function verwaltungMenuTiles(user: AuthUser): ProductMenuTile[] {
  const taken = productMenuPaths(user);
  return platformAdminLinkGroups(user)
    .flatMap((group) =>
      group.links.map((link) => ({
        id: `verwaltung-${link.id}`,
        label: link.label,
        to: link.to,
        group: group.heading,
      })),
    )
    .filter((tile) => !taken.has(tile.to));
}

export function menuModules(user: AuthUser): MenuModule[] {
  const tiles = platformProductTiles(user);
  const modules: MenuModule[] = [{ id: 'overview', label: 'Übersicht' }];
  for (const tile of tiles) {
    if (tile.status === 'live') {
      modules.push({ id: tile.code, label: tile.labelDe });
    }
  }
  if (stammdatenMenuTiles(user).length > 0) {
    modules.push({ id: 'stammdaten', label: 'Stammdaten' });
  }
  if (verwaltungMenuTiles(user).length > 0) {
    modules.push({ id: 'verwaltung', label: 'Verwaltung' });
  }
  for (const tile of tiles) {
    if (tile.status === 'planned') {
      modules.push({ id: tile.code, label: tile.labelDe, soon: true });
    }
  }
  return modules;
}

export function menuModuleForPath(pathname: string): MenuModuleId {
  if (pathname === START_PATH) {
    return 'overview';
  }
  if (pathname === '/stammdaten' || pathname.startsWith('/stammdaten/')) {
    return 'stammdaten';
  }
  if (pathname === '/verwaltung' || pathname.startsWith('/verwaltung/')) {
    return 'verwaltung';
  }
  return activeProductCode(pathname) ?? 'overview';
}

export function tilesForMenuModule(
  moduleId: MenuModuleId,
  user: AuthUser,
  extras: readonly ProductMenuTile[] = [],
): ProductMenuTile[] {
  if (moduleId === 'overview') {
    return [];
  }
  if (moduleId === 'stammdaten') {
    return stammdatenMenuTiles(user);
  }
  if (moduleId === 'verwaltung') {
    return verwaltungMenuTiles(user);
  }
  if (moduleId === 'tools') {
    return [];
  }
  const registration = productNavRegistration(moduleId as never);
  return [...productMenuTiles(registration, user), ...extras];
}

export function buildOverviewCatalog(
  user: AuthUser,
  shortcuts: readonly IntranetQuickAccessItem[] = [],
): Map<string, OverviewTile> {
  const catalog = new Map<string, OverviewTile>();

  function add(tile: { id: string; label: string; to: string; group?: string; icon?: LucideIcon }) {
    if (!catalog.has(tile.to)) {
      catalog.set(tile.to, {
        id: tile.id,
        path: tile.to,
        label: tile.label,
        group: tile.group,
        icon: tile.icon,
      });
    }
  }

  add({ id: 'overview-home', label: 'Übersicht', to: START_PATH });
  for (const tile of platformProductTiles(user)) {
    if (tile.status === 'live' && tile.href) {
      add({
        id: `product-${tile.code}`,
        label: tile.labelDe,
        to: tile.href,
        icon: productIcon(tile.code),
      });
    }
  }
  for (const code of user.products ?? []) {
    for (const tile of productMenuTiles(productNavRegistration(code), user)) {
      add(tile);
    }
  }
  for (const tile of stammdatenMenuTiles(user)) {
    add(tile);
  }
  for (const tile of verwaltungMenuTiles(user)) {
    add(tile);
  }
  for (const item of shortcuts) {
    add({ id: `shortcut-${item.id}`, label: item.label, to: item.url });
  }
  return catalog;
}

export function visibleOverviewTiles(
  paths: readonly string[],
  catalog: Map<string, OverviewTile>,
): OverviewTile[] {
  return paths.flatMap((path) => {
    const tile = catalog.get(path);
    return tile ? [tile] : [];
  });
}
